import type { QueryObserverResult } from '@tanstack/query-core'
import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useCallback } from 'react'
import { z } from 'zod'
import { useDisabledOnsite } from '~/features/reservationInput/store/paymentMethod'
import { isNotBlank } from '~/lib/blank/blank'
import { MINIMUM_IKYU_POINT } from '~/lib/constants/constants'
import type { ValidationStatus } from '~/lib/encoding/shiftjis'
import { ValidateShiftJis, errorMessage as shiftJisErrorMessage } from '~/lib/encoding/shiftjis'
import type { ValidateShiftJisQuery, ValidateShiftJisQueryVariables } from '~/lib/graphql/graphql'
import type {
  ExistEmailRequest,
  ExistsPhoneNumberRequest,
  ValidateEmailRequest,
  ValidateEmailResponse,
  ValidatePhoneNumberRequest,
  ValidatePhoneNumberResponse,
} from '~/lib/member/member'
import {
  MEMBER_STALE_TIME,
  callExistsEmail,
  callExistsPhoneNumber,
  callValidateEmail,
  callValidatePhoneNumber,
} from '~/lib/member/member'
import { env$ } from '~/stores/env'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'
import {
  affiliateSiteFormAnswer$,
  email$,
  ikyuPointUsage$,
  isReserverDifferentFromVisitor$,
  password$,
  reserverNameKana$,
  reserverPhoneNumber$,
  visitorNameKana$,
  visitorPhoneNumber$,
} from './stateMachine/context'
import {
  isGuestOrPreMember$,
  isPreMember$,
  member$,
  reserveInfoQueryFn$,
  useTransition,
} from './stateMachine/state'
import type { ReservationInputEvent } from './stateMachine/statemachine'

/**
 * - 氏名（漢字）の入力欄を撤廃し、reserverName にはよみがなをいれるようにする
 * - 議論：https://github.com/ikyucom/macaron-frontend/issues/2458
 */
export const reserverName$ = atom((get) => {
  return reserverName(get(reserverNameKana$), get(member$)?.name)
})
reserverName$.debugLabel = 'reserverName$'

const reserverName = import.meta.env.VITE_MODE === 'ppg' ? ppgReserverName : ikyuReserverName

function ikyuReserverName(reserverNameKana: string, memberName?: string) {
  // 会員で氏名（漢字）の情報を持っている場合は、それを優先する
  return isNotBlank(memberName) ? memberName : reserverNameKana
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('ikyuReservarName', () => {
    expect(ikyuReserverName('いっきゅうたろう', '一休太郎')).toEqual('一休太郎')
    expect(ikyuReserverName('いっきゅうたろう', undefined)).toEqual('いっきゅうたろう')
    expect(ikyuReserverName('いっきゅうたろう', '')).toEqual('いっきゅうたろう')
  })
}

function ppgReserverName(reserverNameKana: string, _memberName?: string) {
  // PPG では氏名（漢字）に氏名（ひらがな）を入れる
  // https://github.com/ikyucom/macaron-frontend/issues/4847
  return reserverNameKana
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('ppgReservarName', () => {
    expect(ppgReserverName('いっきゅうたろう', '一休太郎')).toEqual('いっきゅうたろう')
    expect(ppgReserverName('いっきゅうたろう', undefined)).toEqual('いっきゅうたろう')
    expect(ppgReserverName('いっきゅうたろう', '')).toEqual('いっきゅうたろう')
  })
}

export const reserverNameError$ = atom((get) => {
  return get(reserverNameKanaError$)
})
reserverNameError$.debugLabel = 'reserverNameError$'

const reserverNameKanaShitJisQueryVariables$: Atom<ValidateShiftJisQueryVariables> = atom((get) => {
  return {
    input: get(reserverNameKana$),
  }
})

export const reserverNameKanaShiftJisQueryFn$ = createQueryAtom<typeof ValidateShiftJis>()
reserveInfoQueryFn$.debugLabel = 'reserverNameKanaShiftJisQueryFn$'

const reserverNameKanaShiftJisQuery$ = atomWithQuery((get) =>
  createQueryOptions(
    ValidateShiftJis,
    get(reserverNameKanaShitJisQueryVariables$),
    get(reserverNameKanaShiftJisQueryFn$).fn,
    { staleTime: 60 * 1000, enabled: get(reserverNameKana$) !== '' }
  )
)
reserverNameKanaShiftJisQuery$.debugLabel = 'reserverNameKanaShiftJisQuery$'

export const reserverNameKanaShitJisQueryResult$: Atom<ValidationStatus> = atom((get) => {
  return validateShiftJisResult(get(reserverNameKanaShiftJisQuery$))
})
reserverNameKanaShitJisQueryResult$.debugLabel = 'reserverNameKanaShitJisQueryResult$'

export function validateShiftJisResult(
  result: QueryObserverResult<ValidateShiftJisQuery, Error>
): ValidationStatus {
  if (result.status === 'pending' || result.status === 'error') {
    return { status: 'unchecked' }
  }
  if (result.data.shiftJis.__typename === 'ShiftJisError') {
    return { status: 'invalid', unmappableCharacter: result.data.shiftJis.unmappableCharacter }
  }
  return { status: 'valid', bytesLen: result.data.shiftJis.bytesLen }
}

export const reserverNameKanaError$ = atom((get) => {
  return validateNameKana(get(reserverNameKanaShitJisQueryResult$), get(reserverNameKana$))
})
reserverNameKanaError$.debugLabel = 'reserverNameKanaError$'

export function useReserverNameKanaError() {
  return useAtomValue(reserverNameKanaError$)
}

// 以下を基に作成
// https://github.com/ikyucom/restaurant/blob/master/frontends/src/common/Validator/reservePage.js#L123
const yomiganaPattern = /^[ぁ-んーa-zA-Zａ-ｚ 　]*$/
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validateYomigana', () => {
    expect(yomiganaPattern.test('ー')).toBe(true)
    expect(yomiganaPattern.test('ああああ')).toBe(true)
    expect(yomiganaPattern.test('ああ　ああ')).toBe(true)
    expect(yomiganaPattern.test('ああ ああ')).toBe(true)
    expect(yomiganaPattern.test('aaaa')).toBe(true)
    expect(yomiganaPattern.test('AAAA')).toBe(true)
    expect(yomiganaPattern.test('アアアア')).toBe(false)
    expect(yomiganaPattern.test('ｱｱｱｱ')).toBe(false)
    expect(yomiganaPattern.test('1111')).toBe(false)
    expect(yomiganaPattern.test('あ1')).toBe(false)
    expect(yomiganaPattern.test('一休太郎')).toBe(false)
    expect(yomiganaPattern.test('いっきゅう 太郎')).toBe(false)
  })
}

const nameKanaSchema = z
  .string()
  .min(1, {
    message: '氏名（ひらがな）は必須項目です',
  })
  .regex(yomiganaPattern, {
    message: '氏名（ひらがな）はひらがな(外国名の方は半角ローマ字)で入力してください',
  })

function validateNameKana(status: ValidationStatus, nameKana: string) {
  const result = nameKanaSchema.safeParse(nameKana)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return shiftJisErrorMessage('氏名（ひらがな）', status, { min: 3, max: 100 })
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validateReserverNameKana', () => {
    expect(validateNameKana({ status: 'unchecked' }, '')).toEqual('氏名（ひらがな）は必須項目です')
    expect(validateNameKana({ status: 'invalid', unmappableCharacter: 'あ' }, 'あ')).toEqual(
      '氏名（ひらがな）に使用できない文字が含まれています: あ'
    )
    expect(validateNameKana({ status: 'valid', bytesLen: 1 }, '')).toEqual(
      '氏名（ひらがな）は必須項目です'
    )
    expect(validateNameKana({ status: 'valid', bytesLen: 2 }, 'ああ')).toEqual(
      '氏名（ひらがな）が短すぎます'
    )
    expect(validateNameKana({ status: 'valid', bytesLen: 8 }, 'いっきゅうたろう')).toEqual('')
  })
}

const reserverPhoneNumberSchema$ = atom((get) => {
  return validatePhoneNumber(get(reserverPhoneNumber$))
})
reserverPhoneNumberSchema$.debugLabel = 'reserverPhoneNumberSchema$'

export const phoneNumberSchema = z
  .string()
  .min(1, { message: '電話番号は必須項目です' })
  .refine((v) => validatePhoneNumberChar(v), {
    message: '電話番号の形式が正しくありません。入力を確認してください',
  })

function validatePhoneNumberChar(v: string) {
  if (!/^\d{10,15}$/.test(v)) {
    return false
  }
  if (/^0[5-9]0/.test(v)) {
    return v.length === 11
  }
  return true
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('validatePhoneNumberChar', () => {
    describe('有効な電話番号', () => {
      it('携帯電話', () => {
        expect(validatePhoneNumberChar('09012345678')).toBe(true)
      })
      it('固定電話/海外', () => {
        expect(validatePhoneNumberChar('0570123456')).toBe(true)
        expect(validatePhoneNumberChar('057012345678900')).toBe(true)
      })
    })
    describe('無効な電話番号', () => {
      it('携帯電話', () => {
        expect(validatePhoneNumberChar('090123456')).toBe(false)
        expect(validatePhoneNumberChar('0901234567')).toBe(false)
        expect(validatePhoneNumberChar('0901234567890')).toBe(false)
        expect(validatePhoneNumberChar('090123456789')).toBe(false)
        expect(validatePhoneNumberChar('09012345678901')).toBe(false)
      })
      it('固定電話/海外', () => {
        expect(validatePhoneNumberChar('057012345')).toBe(false)
        expect(validatePhoneNumberChar('0570123456789012')).toBe(false)
      })
    })
    describe('2024-12から開放される060の携帯電話番号', () => {
      it('正しい形式', () => {
        expect(validatePhoneNumberChar('06012345678')).toBe(true)
      })
      it('桁数の過不足', () => {
        expect(validatePhoneNumberChar('0601234567')).toBe(false)
        expect(validatePhoneNumberChar('060123456789')).toBe(false)
      })
    })
  })
}

function validatePhoneNumber(phoneNumber: string) {
  const result = phoneNumberSchema.safeParse(phoneNumber)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validatePhoneNumber', () => {
    expect(validatePhoneNumber('')).toEqual('電話番号は必須項目です')
    expect(validatePhoneNumber('09012345678')).toEqual('')
    expect(validatePhoneNumber('aaaaaaaaaa')).toEqual(
      '電話番号の形式が正しくありません。入力を確認してください'
    )
  })
}

export function createPhoneNumberBlackListSchema(
  validatePhoneNumber: (data: ValidatePhoneNumberRequest) => Promise<ValidatePhoneNumberResponse>
) {
  return z
    .object({
      phoneNumber: z.string(),
    })
    .superRefine(async (data, ctx) => {
      const result = await validatePhoneNumber({ phoneNumber: data.phoneNumber })
      switch (result.message) {
        case 'NotMobilePhoneNumber':
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: 'NotMobilePhoneNumber',
          })
          break
        case 'BlackPhoneNumber':
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: 'この電話番号はご利用を停止させて頂いております。',
          })
          break
        case 'InvalidPhoneNumber':
          ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: '電話番号が不正です。登録電話番号を変更してください。',
          })
      }
    })
}
if (import.meta.vitest) {
  const { it, expect, assert } = import.meta.vitest
  const phoneNumber = '09001234567'
  it('not mobile phone number', async () => {
    const validatePhoneNumberMock = (): Promise<ValidatePhoneNumberResponse> => {
      return Promise.resolve({ message: 'NotMobilePhoneNumber' })
    }
    const phoneNumberBlackListSchema = createPhoneNumberBlackListSchema(validatePhoneNumberMock)
    const result = await phoneNumberBlackListSchema.safeParseAsync({ phoneNumber })
    if (result.success) {
      assert.fail('expected result.success to be false, but it was true')
      return
    }
    expect(result.error.errors[0].message).toEqual('NotMobilePhoneNumber')
  })
  it('blacklisted phone number', async () => {
    const validatePhoneNumberMock = (): Promise<ValidatePhoneNumberResponse> => {
      return Promise.resolve({ message: 'BlackPhoneNumber' })
    }
    const phoneNumberBlackListSchema = createPhoneNumberBlackListSchema(validatePhoneNumberMock)
    const result = await phoneNumberBlackListSchema.safeParseAsync({ phoneNumber })
    if (result.success) {
      assert.fail('expected result.success to be false, but it was true')
      return
    }
    expect(result.error.errors[0].message).toEqual(
      'この電話番号はご利用を停止させて頂いております。'
    )
  })
  it('invalid phone number', async () => {
    const validatePhoneNumberMock = (): Promise<ValidatePhoneNumberResponse> => {
      return Promise.resolve({ message: 'InvalidPhoneNumber' })
    }
    const phoneNumberBlackListSchema = createPhoneNumberBlackListSchema(validatePhoneNumberMock)
    const result = await phoneNumberBlackListSchema.safeParseAsync({ phoneNumber })
    if (result.success) {
      assert.fail('expected result.success to be false, but it was true')
      return
    }
    expect(result.error.errors[0].message).toEqual(
      '電話番号が不正です。登録電話番号を変更してください。'
    )
  })
  it('valid phone number', async () => {
    const validatePhoneNumberMock = (): Promise<ValidatePhoneNumberResponse> => {
      return Promise.resolve({ message: 'ValidPhoneNumber' })
    }
    const phoneNumberBlackListSchema = createPhoneNumberBlackListSchema(validatePhoneNumberMock)
    const result = await phoneNumberBlackListSchema.safeParseAsync({ phoneNumber })
    expect(result.success).toBe(true)
  })
}

const phoneNumberBlackListQueryVariables$: Atom<ValidatePhoneNumberRequest> = atom((get) => {
  return {
    phoneNumber: get(reserverPhoneNumber$),
  }
})
phoneNumberBlackListQueryVariables$.debugLabel = 'phoneNumberBlackListQueryVariables$'

type PhoneNumberBlackListQueryKey = readonly [string, string, ValidatePhoneNumberRequest]
const phoneNumberBlackListQueryKey$: Atom<PhoneNumberBlackListQueryKey> = atom((get) => {
  const env = get(env$)
  return ['phoneNumberBlackListCheck', env.MEMBER_ORIGIN, get(phoneNumberBlackListQueryVariables$)]
})
phoneNumberBlackListQueryKey$.debugLabel = 'phoneNumberBlackListQueryKey$'

export const reserverPhoneNumberBlackListQueryFn$ = atom({
  fn: async ({ queryKey: [, origin, variables] }: { queryKey: PhoneNumberBlackListQueryKey }) =>
    validatePhoneNumberBlackList(origin, variables.phoneNumber),
})
reserverPhoneNumberBlackListQueryFn$.debugLabel = 'reserverPhoneNumberBlackListQueryFn$'

const reserverPhoneNumberQueryEnabled$ = atom((get) => {
  return blackListQueryEnabled(get(reserverPhoneNumber$), get(reserverPhoneNumberSchema$))
})
reserverPhoneNumberQueryEnabled$.debugLabel = 'reserverPhoneNumberQueryEnabled$'

function blackListQueryEnabled(phoneNumberOrEmail: string, error: string) {
  return phoneNumberOrEmail !== '' && error === ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('blackListQueryEnabled', () => {
    expect(blackListQueryEnabled('09012345678', '')).toBe(true)
    expect(blackListQueryEnabled('', 'error')).toBe(false)
    expect(blackListQueryEnabled('09012345678', 'error')).toBe(false)
    expect(blackListQueryEnabled('', '')).toBe(false)
  })
}

const reserverPhoneNumberBlackListQuery$ = atomWithQuery((get) => ({
  queryKey: get(phoneNumberBlackListQueryKey$),
  queryFn: get(reserverPhoneNumberBlackListQueryFn$).fn,
  staleTime: MEMBER_STALE_TIME,
  // member へ無駄打ちしたくないので電話番号が入力済かつ入力情報がバリデーション済の場合のみBL判定を行う
  enabled: get(reserverPhoneNumberQueryEnabled$),
}))
reserverPhoneNumberBlackListQuery$.debugLabel = 'reserverPhoneNumberBlackListQuery$'

const isReserverPhoneNumberBlacklistChecked$ = atom((get) => {
  return get(reserverPhoneNumberBlackListQuery$).status === 'success'
})
isReserverPhoneNumberBlacklistChecked$.debugLabel = 'isReserverPhoneNumberBlacklistChecked$'

const hasReserverPhoneNumberBlacklistError$ = atom((get) => {
  const result = get(reserverPhoneNumberBlackListQuery$)
  return result.data?.type === 'InValid'
})
hasReserverPhoneNumberBlacklistError$.debugLabel = 'hasReserverPhoneNumberBlacklistError$'

const reserverPhoneNumberBlackListResult$ = atom<PhoneNumberBlackList | undefined>((get) => {
  const error = get(reserverPhoneNumberSchema$)
  if (error !== '') {
    return { type: 'InValid', message: error }
  }
  const result = get(reserverPhoneNumberBlackListQuery$)
  if (result.status === 'pending' || result.status === 'error') {
    return undefined
  }
  return result.data
})
reserverPhoneNumberBlackListResult$.debugLabel = 'reserverPhoneNumberBlackListResult$'

type PhoneNumberBlackList =
  | { type: 'InValid'; message: string }
  | { type: 'Valid'; isMobileNumber: boolean }
export async function validatePhoneNumberBlackList(
  origin: string,
  phoneNumber: string
): Promise<PhoneNumberBlackList> {
  const phoneNumberBlackListSchema = createPhoneNumberBlackListSchema((data) =>
    callValidatePhoneNumber(origin, data)
  )
  const resultBlackList = await phoneNumberBlackListSchema.safeParseAsync({ phoneNumber })
  if (!resultBlackList.success) {
    if (resultBlackList.error.issues[0].message === 'NotMobilePhoneNumber') {
      return { type: 'Valid', isMobileNumber: false }
    }
    return { type: 'InValid', message: resultBlackList.error.issues[0].message }
  }
  return { type: 'Valid', isMobileNumber: true }
}

export const reserverPhoneNumberError$ = atom((get) => {
  const result = get(reserverPhoneNumberBlackListResult$)
  if (result === undefined) {
    return ''
  }
  if (result.type === 'InValid') {
    return result.message
  }
  return ''
})
reserverPhoneNumberError$.debugLabel = 'reserverPhoneNumberError$'

export function useReserverPhoneNumberError() {
  return useAtomValue(reserverPhoneNumberError$)
}

export const isMobilePhoneNumber$ = atom((get) => {
  const result = get(reserverPhoneNumberBlackListResult$)
  if (result === undefined || result.type === 'InValid') {
    return false
  }
  return result.isMobileNumber
})

const existsPhoneNumberQueryVariables$ = atom((get) => {
  return {
    phoneNumber: get(reserverPhoneNumber$),
  }
})
existsPhoneNumberQueryVariables$.debugLabel = 'existsPhoneNumberQueryVariables$'

type ExistsPhoneNumberQueryKey = readonly [string, string, ExistsPhoneNumberRequest]

const existsPhoneNumberQueryKey$: Atom<ExistsPhoneNumberQueryKey> = atom((get) => {
  const env = get(env$)
  return ['existsPhoneNumber', env.MEMBER_ORIGIN, get(existsPhoneNumberQueryVariables$)]
})
existsPhoneNumberQueryKey$.debugLabel = 'existsPhoneNumberQueryKey$'

export const phoneNumberExitsQueryFn$ = atom({
  fn: async ({ queryKey: [, origin, variables] }: { queryKey: ExistsPhoneNumberQueryKey }) =>
    callExistsPhoneNumber(origin, variables),
})
phoneNumberExitsQueryFn$.debugLabel = 'phoneNumberExitsQueryFn$'

const phoneNumberExistsQueryEnabled$ = atom((get) => {
  return phoneNumberExistsQueryEnabled(
    get(reserverPhoneNumber$),
    get(reserverPhoneNumberError$),
    get(isGuestOrPreMember$)
  )
})
phoneNumberExistsQueryEnabled$.debugLabel = 'phoneNumberExistsQueryEnabled$'

function phoneNumberExistsQueryEnabled(
  phoneNumber: string,
  error: string,
  isGuestOrPreMember: boolean
) {
  return phoneNumber !== '' && error === '' && isGuestOrPreMember
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('phoneNumberExistsQueryEnabled', () => {
    expect(phoneNumberExistsQueryEnabled('09012345678', '', true)).toBe(true)
    expect(phoneNumberExistsQueryEnabled('09012345678', '', false)).toBe(false)
    expect(phoneNumberExistsQueryEnabled('09012345678', 'エラー', true)).toBe(false)
    expect(phoneNumberExistsQueryEnabled('09012345678', 'エラー', false)).toBe(false)
    expect(phoneNumberExistsQueryEnabled('', '', true)).toBe(false)
    expect(phoneNumberExistsQueryEnabled('', '', false)).toBe(false)
    expect(phoneNumberExistsQueryEnabled('', 'エラー', true)).toBe(false)
    expect(phoneNumberExistsQueryEnabled('', 'エラー', false)).toBe(false)
  })
}

const existsPhoneNumberQuery$ = atomWithQuery((get) => ({
  queryKey: get(existsPhoneNumberQueryKey$),
  queryFn: get(phoneNumberExitsQueryFn$).fn,
  staleTime: MEMBER_STALE_TIME,
  // member へ無駄打ちしたくないのでゲストかつ電話番号が入力済かつ入力情報がバリデーション済の場合のみ判定を行う
  enabled: import.meta.env.VITE_MODE === 'ikyu' && get(phoneNumberExistsQueryEnabled$),
}))
existsPhoneNumberQuery$.debugLabel = 'existsPhoneNumberQuery$'

export const existsPhoneNumberResult$ = atom((get) => {
  const result = get(existsPhoneNumberQuery$)
  if (result.status === 'pending' || result.status === 'error') {
    return undefined
  }
  return result.data
})
existsPhoneNumberResult$.debugLabel = 'existsPhoneNumberResult$'

export const isPhoneNumberRegistered$ = atom((get) => {
  const result = get(existsPhoneNumberResult$)
  if (result === undefined) {
    return false
  }
  return result.exists
})
isPhoneNumberRegistered$.debugLabel = 'isPhoneNumberRegistered$'

export function useIsPhoneNumberRegistered() {
  return useAtomValue(isPhoneNumberRegistered$)
}

export const emailSchema = z
  .string()
  .min(1, { message: 'メールアドレスは必須項目です' })
  .email({ message: 'メールアドレスが有効なメールアドレスではありません' })

const emailSchema$ = atom((get) => {
  return validateEmail(get(email$))
})
emailSchema$.debugLabel = 'emailSchema$'

function validateEmail(email: string) {
  const result = emailSchema.safeParse(email)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validateEmail', () => {
    expect(validateEmail('test@ikyu.com')).toEqual('')
    expect(validateEmail('')).toEqual('メールアドレスは必須項目です')
    expect(validateEmail('test@ikyu')).toEqual('メールアドレスが有効なメールアドレスではありません')
  })
}

export function createEmailBlackListSchema(
  validateEmail: (data: ValidateEmailRequest) => Promise<ValidateEmailResponse>
) {
  return z
    .object({
      email: z.string(),
    })
    .superRefine(async (data, ctx) => {
      const result = await validateEmail({ mailAddress: data.email })
      if (result.message === 'BlackMailAddress') {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message: 'このメールアドレスはご利用を停止させて頂いております。',
        })
      }
    })
}
if (import.meta.vitest) {
  const { it, expect, assert } = import.meta.vitest
  const email = 'test@ikyu.com'
  it('blacklisted email address', async () => {
    const validateEmailMock = (): Promise<ValidateEmailResponse> => {
      return Promise.resolve({ message: 'BlackMailAddress' })
    }
    const emailBlackListSchema = createEmailBlackListSchema(validateEmailMock)
    const result = await emailBlackListSchema.safeParseAsync({ email })
    if (result.success) {
      assert.fail('expected result.success to be false, but it was true')
      return
    }
    expect(result.error.errors[0].message).toEqual(
      'このメールアドレスはご利用を停止させて頂いております。'
    )
  })
  it('valid email address', async () => {
    const validateEmailMock = (): Promise<ValidateEmailResponse> => {
      return Promise.resolve({ message: 'ValidMailAddress' })
    }
    const emailBlackListSchema = createEmailBlackListSchema(validateEmailMock)
    const result = await emailBlackListSchema.safeParseAsync({ email })
    expect(result.success).toBe(true)
  })
}

const emailBlackListQueryVariables$: Atom<ValidateEmailRequest> = atom((get) => {
  return {
    mailAddress: get(email$),
  }
})
emailBlackListQueryVariables$.debugLabel = 'emailBlackListQueryVariables$'

type EmailBlackListQueryKey = readonly [string, string, ValidateEmailRequest]
const emailBlackListQueryKey$: Atom<EmailBlackListQueryKey> = atom((get) => {
  const env = get(env$)
  return ['emailBlackListCheck', env.MEMBER_ORIGIN, get(emailBlackListQueryVariables$)]
})
emailBlackListQueryKey$.debugLabel = 'emailBlackListQueryKey$'

export const emailBlackListQueryFn$ = atom({
  fn: async ({ queryKey: [, origin, variables] }: { queryKey: EmailBlackListQueryKey }) =>
    validateEmailBlackList(origin, variables.mailAddress),
})
emailBlackListQueryFn$.debugLabel = 'emailBlackListQueryFn$'

const emailBlackListQueryEnabled$ = atom((get) => {
  return blackListQueryEnabled(get(email$), get(emailSchema$))
})
emailBlackListQueryEnabled$.debugLabel = 'emailBlackListQueryEnabled$'

const emailBlackListQuery$ = atomWithQuery((get) => ({
  queryKey: get(emailBlackListQueryKey$),
  queryFn: get(emailBlackListQueryFn$).fn,
  staleTime: MEMBER_STALE_TIME,
  // member へ無駄打ちしたくないのでメールアドレスが入力済かつ入力情報がバリデーション済の場合のみBL判定を行う
  enabled: get(emailBlackListQueryEnabled$),
}))
emailBlackListQuery$.debugLabel = 'emailBlackListQuery$'

const isEmailBlacklistChecked$ = atom((get) => {
  return get(emailBlackListQuery$).status === 'success'
})
isEmailBlacklistChecked$.debugLabel = 'isEmailBlacklistChecked$'

const hasEmailBlacklistError$ = atom((get) => {
  const result = get(emailBlackListQuery$)
  return result.data?.type === 'InValid'
})
hasEmailBlacklistError$.debugLabel = 'hasEmailBlacklistError$'

export const emailError$ = atom((get) => {
  const error = get(emailSchema$)
  // 入力した値のバリデーションチェック
  if (error !== '') {
    return error
  }
  // ブラックリストチェック
  return createBlackListEmailError(get(emailBlackListQuery$))
})
emailError$.debugLabel = 'emailError$'

type EmailBlackListResult = QueryObserverResult<EmailBlackList, unknown>
function createBlackListEmailError(result: EmailBlackListResult) {
  if (result.status === 'pending' || result.status === 'error') {
    return ''
  }
  if (result.data.type === 'InValid') {
    return result.data.message
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('createBlackListEmailError', () => {
    expect(createBlackListEmailError({ status: 'pending' } as EmailBlackListResult)).toEqual('')
    expect(createBlackListEmailError({ status: 'error' } as EmailBlackListResult)).toEqual('')
    expect(
      createBlackListEmailError({
        status: 'success',
        data: { type: 'InValid', message: 'error' },
      } as EmailBlackListResult)
    ).toEqual('error')
    expect(
      createBlackListEmailError({
        status: 'success',
        data: { type: 'Valid' },
      } as EmailBlackListResult)
    ).toEqual('')
  })
}

export function useEmailError() {
  return useAtomValue(emailError$)
}

type EmailBlackList = { type: 'Valid' } | { type: 'InValid'; message: string }

export async function validateEmailBlackList(
  origin: string,
  email: string
): Promise<EmailBlackList> {
  const emailBlackListSchema = createEmailBlackListSchema((data) => callValidateEmail(origin, data))
  const resultBlackList = await emailBlackListSchema.safeParseAsync({ email })
  if (!resultBlackList.success) {
    return { type: 'InValid', message: resultBlackList.error.issues[0].message }
  }
  // TanStack Query は undefined を返すとエラー扱いになるため、成功時は Valid を返す
  return { type: 'Valid' }
}

const passwordSchema = z
  .string()
  .min(1, { message: 'パスワードは必須項目です' })
  .min(8, { message: 'パスワードは8文字以上で入力してください' })
  .max(20, { message: 'パスワードは20文字以下で入力してください' })
  .regex(new RegExp(/^[\w@!?+.,=*-]*$/), {
    message:
      'パスワードに利用できない文字があります。使えるのは半角英数字・記号（@!?_+.,=*-）です。',
  })
  .refine(
    (v) => {
      const charTypes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[@!?_+.,=*-]/]
      return charTypes.filter((type) => type.test(v)).length >= 2
    },
    {
      message:
        'パスワードは半角【英大文字、英小文字、英数字、記号】4種類より2種類以上を使用している必要があります',
    }
  )

type ExistsEmailQueryKey = readonly [string, string, ExistEmailRequest]

const existsEmailQueryVariables$ = atom((get) => {
  return {
    mailAddress: get(email$),
  }
})
existsEmailQueryVariables$.debugLabel = 'existsEmailQueryVariables$'

const existsEmailQueryKey$: Atom<ExistsEmailQueryKey> = atom((get) => {
  const env = get(env$)
  return ['existsEmail', env.MEMBER_ORIGIN, get(existsEmailQueryVariables$)]
})
existsEmailQueryKey$.debugLabel = 'existsEmailQueryKey$'

export const existsEmailQueryFn$ = atom({
  fn: async ({ queryKey: [, origin, variables] }: { queryKey: ExistsEmailQueryKey }) =>
    callExistsEmail(origin, variables),
})
existsEmailQueryFn$.debugLabel = 'existsEmailQueryFn$'

const emailExistsQueryEnabled$ = atom((get) => {
  return emailExistsQueryEnabled(get(email$), get(emailError$), get(isGuestOrPreMember$))
})
emailExistsQueryEnabled$.debugLabel = 'emailExistsQueryEnabled$'

function emailExistsQueryEnabled(email: string, error: string, isGuestOrPreMember: boolean) {
  return email !== '' && error === '' && isGuestOrPreMember
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('emailExistsQueryEnabled', () => {
    expect(emailExistsQueryEnabled('', 'エラー', true)).toBe(false)
    expect(emailExistsQueryEnabled('', 'エラー', false)).toBe(false)
    expect(emailExistsQueryEnabled('', '', true)).toBe(false)
    expect(emailExistsQueryEnabled('', '', false)).toBe(false)
    expect(emailExistsQueryEnabled('ikyu@ikyu.com', 'エラー', true)).toBe(false)
    expect(emailExistsQueryEnabled('ikyu@ikyu.com', 'エラー', false)).toBe(false)
    expect(emailExistsQueryEnabled('ikyu@ikyu.com', '', true)).toBe(true)
    expect(emailExistsQueryEnabled('ikyu@ikyu.com', '', false)).toBe(false)
  })
}

const existsEmailQuery$ = atomWithQuery((get) => ({
  queryKey: get(existsEmailQueryKey$),
  queryFn: get(existsEmailQueryFn$).fn,
  staleTime: MEMBER_STALE_TIME,
  // member へ無駄打ちしたくないのでゲストかつメールアドレスが入力済かつ入力情報がバリデーション済の場合のみ判定を行う
  enabled: import.meta.env.VITE_MODE === 'ikyu' && get(emailExistsQueryEnabled$),
}))
existsEmailQuery$.debugLabel = 'emailExistsQuery$'

export const existsEmailResult$ = atom((get) => {
  const result = get(existsEmailQuery$)
  if (result.status === 'pending' || result.status === 'error') {
    return undefined
  }
  return result.data
})
existsEmailResult$.debugLabel = 'existsEmailResult$'

export const isEmailRegistered$ = atom((get) => {
  const result = get(existsEmailResult$)
  if (result === undefined) {
    return false
  }
  return result.exists
})
isEmailRegistered$.debugLabel = 'isEmailRegistered$'

export function useIsEmailRegistered() {
  return useAtomValue(isEmailRegistered$)
}

export const isBlacklistChecked$ = atom((get) => {
  return get(isEmailBlacklistChecked$) && get(isReserverPhoneNumberBlacklistChecked$)
})

export const hasBlacklistError$ = atom((get) => {
  return get(hasEmailBlacklistError$) || get(hasReserverPhoneNumberBlacklistError$)
})

export const passwordError$ = atom((get) => {
  return validatePassword(get(isPreMember$), get(password$))
})
passwordError$.debugLabel = 'passwordError$'

export function usePasswordError() {
  return useAtomValue(passwordError$)
}

function validatePassword(isPreMember: boolean, password: string) {
  // 会員登録するゲスト以外の場合パスワード入力項目が表示されず、
  // 必須項目ではなくなるので、エラーメッセージを表示しない
  if (!isPreMember) {
    return ''
  }
  const result = passwordSchema.safeParse(password)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('validatePasswordInput', () => {
    it('PreMemberではない', () => {
      expect(validatePassword(false, 'p@ssw0rd')).toEqual('')
    })
    it('8文字より少ない', () => {
      expect(validatePassword(true, '1234567')).toEqual('パスワードは8文字以上で入力してください')
    })
    it('20文字より多い', () => {
      expect(validatePassword(true, '123456789012345678901')).toEqual(
        'パスワードは20文字以下で入力してください'
      )
    })
    it('利用できない文字がある', () => {
      expect(validatePassword(true, '1234567★345678901')).toEqual(
        'パスワードに利用できない文字があります。使えるのは半角英数字・記号（@!?_+.,=*-）です。'
      )
    })
    it('半角【英大文字、英小文字、英数字、記号】4種類から1種類しか使用していない', () => {
      expect(validatePassword(true, '1234567345678901')).toEqual(
        'パスワードは半角【英大文字、英小文字、英数字、記号】4種類より2種類以上を使用している必要があります'
      )
    })
    it('半角【英大文字、英小文字】を使用', () => {
      expect(validatePassword(true, 'Password')).toEqual('')
    })
    it('半角【英大文字、英数字】を使用', () => {
      expect(validatePassword(true, 'PASSWORD2')).toEqual('')
    })
    it('半角【英大文字、記号】を使用', () => {
      expect(validatePassword(true, 'P@SSWORD')).toEqual('')
    })
    it('半角【英小文字、英数字】を使用', () => {
      expect(validatePassword(true, 'password1')).toEqual('')
    })
    it('半角【英小文字、記号】を使用', () => {
      expect(validatePassword(true, 'p@ssword')).toEqual('')
    })
    it('半角【英数字、記号】を使用', () => {
      expect(validatePassword(true, '1234567@')).toEqual('')
    })
    it('パスワード入力しない', () => {
      expect(validatePassword(true, '')).toEqual('パスワードは必須項目です')
    })
  })
}

const hasMemberName$ = atom((get) => {
  const member = get(member$)
  return isNotBlank(member?.name)
})
hasMemberName$.debugLabel = 'hasMemberName$'

const hasMemberNameKana$ = atom((get) => {
  const member = get(member$)
  return isNotBlank(member?.nameKana)
})
hasMemberNameKana$.debugLabel = 'hasMemberNameKana$'

const hasMemberPhoneNumber$ = atom((get) => {
  const member = get(member$)
  return isNotBlank(member?.phoneNumber)
})
hasMemberPhoneNumber$.debugLabel = 'hasMemberPhoneNumber$'

const hasMemberEmail$ = atom((get) => {
  const member = get(member$)
  return isNotBlank(member?.email)
})
hasMemberEmail$.debugLabel = 'hasMemberEmail$'

/**
 * 予約入力に必要な会員情報が全て揃っているか
 *
 * 氏名（漢字）の入力欄撤廃に伴い、氏名（漢字）の有無は考慮しない
 */
const isMemberInfoComplete$ = atom((get) => {
  return get(hasMemberNameKana$) && get(hasMemberPhoneNumber$) && get(hasMemberEmail$)
})
isMemberInfoComplete$.debugLabel = 'isMemberInfoComplete$'

export type MemberInputStatus =
  //
  // # 氏名（よみがな）
  // いっきゅうたろう
  // # 電話番号
  // 09000000000
  // # メールアドレス
  // （入力フォーム）
  //
  //入力フォームの場合はヘッダを常に表示（この例ではメールアドレス）
  | { type: 'input' }
  //
  // # 氏名（よみがな）
  // いっきゅうたろう
  // # 電話番号
  // 09000000000
  // # メールアドレス
  // （入力フォーム）
  //
  // 他の項目に入力フォームがある場合、入力にだけヘッダを表示するとバランスが悪いので、
  // すべてヘッダを出す（この例の場合、氏名・電話番号が valueWithHeader となる）
  | { type: 'valueWithHeader'; value: string }
  //
  // 一休太郎（いっきゅうたろう）
  // 09000000000
  // test@ikyu.com
  //
  // すべて揃ってる場合、高さを削るために header なしで value を表示する
  // (上記の例のように中身を見れば、何の情報を出しているかわかるため）
  | { type: 'valueOnly'; value: string }

const memberNameStatus$ = atom((get) => {
  return createMemberNameStatus({
    hasName: get(hasMemberName$),
    isMemberInfoComplete: get(isMemberInfoComplete$),
    name: get(reserverName$),
  })
})
memberNameStatus$.debugLabel = 'memberNameStatus$'

export function useMemberNameStatus() {
  return useAtomValue(memberNameStatus$)
}

function createMemberNameStatus(condition: {
  hasName: boolean
  isMemberInfoComplete: boolean
  name: string
}): MemberInputStatus {
  const { hasName, name, isMemberInfoComplete } = condition
  if (!hasName) {
    return { type: 'input' }
  }
  if (!isMemberInfoComplete) {
    return { type: 'valueWithHeader', value: name }
  }
  return { type: 'valueOnly', value: name }
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('createMemberNameInputStatus', () => {
    it('氏名が未登録の会員', () => {
      const condition = {
        hasName: false,
      } as Parameters<typeof createMemberNameStatus>[0]
      expect(createMemberNameStatus(condition)).toEqual({ type: 'input' })
    })
    it('氏名(漢字)が登録済、他の項目が未登録の会員', () => {
      const condition = {
        hasName: true,
        isMemberInfoComplete: false,
        name: '一休太郎',
      } as Parameters<typeof createMemberNameStatus>[0]
      expect(createMemberNameStatus(condition)).toEqual({
        type: 'valueWithHeader',
        value: '一休太郎',
      })
    })
    it('全ての項目が登録済の会員', () => {
      const condition = {
        hasName: true,
        hasNameKana: true,
        isMemberInfoComplete: true,
        name: '一休太郎',
        nameKana: 'いっきゅうたろう',
      } as Parameters<typeof createMemberNameStatus>[0]
      expect(createMemberNameStatus(condition)).toEqual({
        type: 'valueOnly',
        value: '一休太郎',
      })
    })
  })
}

const paypayMemberNameKanaStatus$ = atom<MemberInputStatus>((get) => {
  return { type: 'input' }
})

const ikyuMemberNameKanaStatus$ = atom((get) => {
  const condition = {
    hasName: get(hasMemberName$),
    hasNameKana: get(hasMemberNameKana$),
    isMemberInfoComplete: get(isMemberInfoComplete$),
    name: get(reserverName$),
    nameKana: get(reserverNameKana$),
  }
  return createMemberNameKanaStatus(condition)
})

function createMemberNameKanaStatus(condition: {
  hasName: boolean
  hasNameKana: boolean
  isMemberInfoComplete: boolean
  name: string
  nameKana: string
}): MemberInputStatus {
  const { hasName, hasNameKana, isMemberInfoComplete, name, nameKana } = condition
  if (!hasNameKana) {
    return { type: 'input' }
  }
  if (!isMemberInfoComplete) {
    return { type: 'valueWithHeader', value: nameKana }
  }
  // 稀なケースだが name が未登録で nameKana が登録されている場合があり得る。
  // （氏名が未入力で、氏名(カナ)が入力されているYIDでアカウント連携して登録された会員の場合など）
  // この場合は name の表示箇所に nameKana を表示する。
  const displayName = `${hasName ? name : nameKana} （${nameKana}）`
  return { type: 'valueOnly', value: displayName }
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('createMemberNameKanaInputStatus', () => {
    it('氏名(かな)が未登録の会員', () => {
      const condition = {
        hasNameKana: false,
      } as Parameters<typeof createMemberNameKanaStatus>[0]
      expect(createMemberNameKanaStatus(condition)).toEqual({ type: 'input' })
    })
    it('氏名(かな)が登録済、他の項目が未登録の会員', () => {
      const condition = {
        hasNameKana: true,
        isMemberInfoComplete: false,
        nameKana: 'いっきゅうたろう',
      } as Parameters<typeof createMemberNameKanaStatus>[0]
      expect(createMemberNameKanaStatus(condition)).toEqual({
        type: 'valueWithHeader',
        value: 'いっきゅうたろう',
      })
    })
    it('全ての項目が登録済の会員', () => {
      const condition = {
        hasName: true,
        hasNameKana: true,
        isMemberInfoComplete: true,
        name: '一休太郎',
        nameKana: 'いっきゅうたろう',
      } as Parameters<typeof createMemberNameKanaStatus>[0]
      expect(createMemberNameKanaStatus(condition)).toEqual({
        type: 'valueOnly',
        value: '一休太郎 （いっきゅうたろう）',
      })
    })
    it('氏名(漢字)を除く全ての項目が登録済の会員', () => {
      const condition = {
        hasName: false,
        hasNameKana: true,
        isMemberInfoComplete: true,
        nameKana: 'いっきゅうたろう',
      } as Parameters<typeof createMemberNameKanaStatus>[0]
      expect(createMemberNameKanaStatus(condition)).toEqual({
        type: 'valueOnly',
        value: 'いっきゅうたろう （いっきゅうたろう）',
      })
    })
  })
}

const memberNameKanaStatus$ =
  import.meta.env.VITE_MODE === 'ppg' ? paypayMemberNameKanaStatus$ : ikyuMemberNameKanaStatus$
memberNameKanaStatus$.debugLabel = 'memberNameKanaStatus$'

export function useMemberNameKanaStatus() {
  return useAtomValue(memberNameKanaStatus$)
}

const ikyuMemberPhoneNumberStatus$ = atom((get) => {
  const condition = {
    hasPhoneNumber: get(hasMemberPhoneNumber$),
    isMemberInfoComplete: get(isMemberInfoComplete$),
    phoneNumber: get(reserverPhoneNumber$),
  }
  return createMemberPhoneNumberStatus(condition)
})

function createMemberPhoneNumberStatus(condition: {
  hasPhoneNumber: boolean
  isMemberInfoComplete: boolean
  phoneNumber: string
}): MemberInputStatus {
  const { hasPhoneNumber, isMemberInfoComplete, phoneNumber } = condition
  if (!hasPhoneNumber) {
    return { type: 'input' }
  }
  if (!isMemberInfoComplete) {
    return { type: 'valueWithHeader', value: phoneNumber }
  }
  return { type: 'valueOnly', value: phoneNumber }
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('createMemberPhoneNumberStatus', () => {
    it('電話番号が未登録の会員', () => {
      const condition = {
        hasPhoneNumber: false,
      } as Parameters<typeof createMemberPhoneNumberStatus>[0]
      expect(createMemberPhoneNumberStatus(condition)).toEqual({ type: 'input' })
    })
    it('電話番号が登録済、他の項目が未登録の会員', () => {
      const condition = {
        hasPhoneNumber: true,
        isMemberInfoComplete: false,
        phoneNumber: '09001234567',
      } as Parameters<typeof createMemberPhoneNumberStatus>[0]
      expect(createMemberPhoneNumberStatus(condition)).toEqual({
        type: 'valueWithHeader',
        value: '09001234567',
      })
    })
    it('全ての項目が登録済の会員', () => {
      const condition = {
        hasPhoneNumber: true,
        isMemberInfoComplete: true,
        phoneNumber: '09001234567',
      } as Parameters<typeof createMemberPhoneNumberStatus>[0]
      expect(createMemberPhoneNumberStatus(condition)).toEqual({
        type: 'valueOnly',
        value: '09001234567',
      })
    })
  })
}

const paypayMemberPhoneNumberStatus$ = atom<MemberInputStatus>((get) => {
  return { type: 'input' }
})

const memberPhoneNumberStatus$ =
  import.meta.env.VITE_MODE === 'ppg'
    ? paypayMemberPhoneNumberStatus$
    : ikyuMemberPhoneNumberStatus$
memberPhoneNumberStatus$.debugLabel = 'memberPhoneNumberStatus$'

export function useMemberPhoneNumberStatus() {
  return useAtomValue(memberPhoneNumberStatus$)
}

const paypayMemberEmailStatus$ = atom<MemberInputStatus>((get) => {
  // PayPay グルメのヤフー会員は必ずメールアドレスが登録されているため、入力フォームは表示しない
  return { type: 'valueWithHeader', value: get(email$) }
})
paypayMemberEmailStatus$.debugLabel = 'paypayMemberEmailStatus$'

const ikyuMemberEmailStatus$ = atom<MemberInputStatus>((get) => {
  const condition = {
    hasEmail: get(hasMemberEmail$),
    isMemberInfoComplete: get(isMemberInfoComplete$),
    email: get(email$),
  }
  return createMemberEmailStatus(condition)
})

function createMemberEmailStatus(condition: {
  hasEmail: boolean
  isMemberInfoComplete: boolean
  email: string
}): MemberInputStatus {
  const { hasEmail, isMemberInfoComplete, email } = condition
  if (!hasEmail) {
    return { type: 'input' }
  }
  if (!isMemberInfoComplete) {
    return { type: 'valueWithHeader', value: email }
  }
  return { type: 'valueOnly', value: email }
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('createMemberEmailStatus', () => {
    it('メールアドレスが未登録の会員', () => {
      const condition = {
        hasEmail: false,
      } as Parameters<typeof createMemberEmailStatus>[0]
      expect(createMemberEmailStatus(condition)).toEqual({ type: 'input' })
    })
    it('メールアドレスが登録済、他の項目が未登録の会員', () => {
      const condition = {
        hasEmail: true,
        isMemberInfoComplete: false,
        email: 'test@ikyu.com',
      } as Parameters<typeof createMemberEmailStatus>[0]
      expect(createMemberEmailStatus(condition)).toEqual({
        type: 'valueWithHeader',
        value: 'test@ikyu.com',
      })
    })
    it('全ての項目が登録済の会員', () => {
      const condition = {
        hasEmail: true,
        isMemberInfoComplete: true,
        email: 'test@ikyu.com',
      } as Parameters<typeof createMemberEmailStatus>[0]
      expect(createMemberEmailStatus(condition)).toEqual({
        type: 'valueOnly',
        value: 'test@ikyu.com',
      })
    })
  })
}

const memberEmailStatus$ =
  import.meta.env.VITE_MODE === 'ppg' ? paypayMemberEmailStatus$ : ikyuMemberEmailStatus$

export function useMemberEmailStatus() {
  return useAtomValue(memberEmailStatus$)
}

/**
 * - 氏名（漢字）の入力欄を撤廃し、visitorName にはよみがなをいれるようにする
 * - 議論：https://github.com/ikyucom/macaron-frontend/issues/2458
 */
export const visitorName$ = atom((get) => {
  return get(visitorNameKana$)
})
visitorName$.debugLabel = 'visitorName$'

const visitorNameKanaShitJisQueryVariables$: Atom<ValidateShiftJisQueryVariables> = atom((get) => {
  return {
    input: get(visitorNameKana$),
  }
})
visitorNameKanaShitJisQueryVariables$.debugLabel = 'visitorNameKanaShitJisQueryVariables$'

const visitorNameKanaShiftJisQueryFn$ = createQueryAtom<typeof ValidateShiftJis>()
visitorNameKanaShiftJisQueryFn$.debugLabel = 'visitorNameKanaShiftJisQueryFn$'

const visitorNameKanaShiftJisQuery$ = atomWithQuery((get) =>
  createQueryOptions(
    ValidateShiftJis,
    get(visitorNameKanaShitJisQueryVariables$),
    get(visitorNameKanaShiftJisQueryFn$).fn,
    { staleTime: 60 * 1000, enabled: get(visitorNameKana$) !== '' }
  )
)
visitorNameKanaShiftJisQuery$.debugLabel = 'visitorNameKanaShiftJisQuery$'

const visitorNameKanaShitJisQueryResult$: Atom<ValidationStatus> = atom((get) => {
  return validateShiftJisResult(get(visitorNameKanaShiftJisQuery$))
})
visitorNameKanaShitJisQueryResult$.debugLabel = 'visitorNameKanaShitJisQueryResult$'

export const visitorNameKanaError$ = atom((get) => {
  if (!get(isReserverDifferentFromVisitor$)) {
    return ''
  }
  return validateNameKana(get(visitorNameKanaShitJisQueryResult$), get(visitorNameKana$))
})
visitorNameKanaError$.debugLabel = 'visitorNameKanaError$'

export function useVisitorNameKanaError() {
  return useAtomValue(visitorNameKanaError$)
}

export const visitorPhoneNumberError$ = atom((get) => {
  if (!get(isReserverDifferentFromVisitor$)) {
    return ''
  }
  return validatePhoneNumber(get(visitorPhoneNumber$))
})
visitorPhoneNumberError$.debugLabel = 'visitorPhoneNumberError$'

export function useVisitorPhoneNumberError() {
  return useAtomValue(visitorPhoneNumberError$)
}

export const ikyuPointUsageError$ = atom((get) => {
  const point = get(ikyuPointUsage$)
  return validateIkyuPoint(point)
})
ikyuPointUsageError$.debugLabel = 'ikyuPointUsageError$'

export function useIkyuPointUsageError() {
  return useAtomValue(ikyuPointUsageError$)
}

const ikyuPointSchema = z
  .number()
  .min(MINIMUM_IKYU_POINT, { message: `ポイントは${MINIMUM_IKYU_POINT}ポイントからご利用できます` })

export function validateIkyuPoint(point: number) {
  if (point === 0) {
    return ''
  }
  const result = ikyuPointSchema.safeParse(point)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validateIkyuPoint', () => {
    expect(validateIkyuPoint(0)).toBe('')
    expect(validateIkyuPoint(1)).toBe('ポイントは10ポイントからご利用できます')
    expect(validateIkyuPoint(10)).toBe('')
  })
}

// 提携サイトの入力フォームは、提携サイトによって入力する値が異なるため
// 文字数チェックのみする
const affiliateSiteFormAnswerSchema = z
  .string()
  .max(50, { message: '50文字以内で入力してください' })

const affiliateSiteFormAnswerError$ = atom((get) => {
  return validateAffiliateSiteFormAnswer(get(affiliateSiteFormAnswer$))
})
affiliateSiteFormAnswerError$.debugLabel = 'affiliateSiteFormAnswerError$'

export function useAffiliateSiteFormAnswerError() {
  return useAtomValue(affiliateSiteFormAnswerError$)
}

function validateAffiliateSiteFormAnswer(answer: string) {
  const result = affiliateSiteFormAnswerSchema.safeParse(answer)
  if (!result.success) {
    return result.error.errors[0].message
  }
  return ''
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validateAffiliateSiteFormAnswer', () => {
    expect(validateAffiliateSiteFormAnswer('')).toBe('')
    expect(validateAffiliateSiteFormAnswer('a'.repeat(51))).toBe('50文字以内で入力してください')
    expect(validateAffiliateSiteFormAnswer('a'.repeat(50))).toBe('')
  })
}

export function useSetReserverNameKana() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        reserverNameKana: value,
      })
    },
    [send]
  )
}

export function useSetReserverPhoneNumber() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        reserverPhoneNumber: value,
      })
    },
    [send]
  )
}

export function useSetEmail() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        email: value,
      })
    },
    [send]
  )
}

export function useSetPassword() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        password: value,
      })
    },
    [send]
  )
}

export function useSetVisitorNameKana() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        visitorNameKana: value,
      })
    },
    [send]
  )
}

export function useSetVisitorPhoneNumber() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        visitorPhoneNumber: value,
      })
    },
    [send]
  )
}

export function useSetIsReserverDifferentFromVisitor() {
  const send = useTransition()
  return useCallback(
    (value: boolean) => {
      send({
        type: 'updated',
        isReserverDifferentFromVisitor: value,
      })
    },
    [send]
  )
}

export function useSetAffiliateSiteFormAnswer() {
  const send = useTransition()
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        affiliateSiteFormAnswer: value,
      })
    },
    [send]
  )
}

export function useTransitionPreMemberChecked() {
  const send = useTransition()
  const onsiteDisabled = useDisabledOnsite()
  return useCallback(
    (v: boolean) => {
      send(preMemberChangedEvent(v, onsiteDisabled))
    },
    [onsiteDisabled, send]
  )
}

function preMemberChangedEvent(
  registerAsMember: boolean,
  onsiteDisabled: boolean
): ReservationInputEvent {
  return {
    type: 'preMemberChanged',
    paymentMethod: registerAsMember
      ? // ゲストから会員登録かつ現地決済不可の場合、支払い方法をカード決済に変更する必要がある
        onsiteDisabled
        ? 'ONLINE'
        : 'ONSITE'
      : // ゲストはカード決済できないので必ず現地決済にする
        // 現地決済不可の店舗でも現地決済にする
        // ゲストは現地決済を前提にしているので、ゲストでカード決済にしてしまうと想定しない不具合が起こりうるため
        // 他の atom でゲストかつ現地決済不可の場合は予約できないように制御している
        'ONSITE',
  }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('preMemberChangedEvent', () => {
    // ゲストから会員登録ON・現地決済NG
    expect(preMemberChangedEvent(true, true)).toStrictEqual({
      type: 'preMemberChanged',
      paymentMethod: 'ONLINE',
    })
    // ゲストから会員登録ON・即時利用NG
    expect(preMemberChangedEvent(true, false)).toStrictEqual({
      type: 'preMemberChanged',
      paymentMethod: 'ONSITE',
    })
    // ゲストから会員登録OFF・現地決済NG
    expect(preMemberChangedEvent(false, true)).toStrictEqual({
      type: 'preMemberChanged',
      paymentMethod: 'ONSITE',
    })
  })
}
